"use client";

import { useMemo } from "react";

import {
  buildUnitGroups,
  resolveEntryUnitIds,
  unitIdsOfGroup,
  type GroupableUnit,
  type UnitGroupConfig,
  type UnitGroupingMode,
} from "@autodromo/units";

import { useUnitSelection } from "./useUnitSelection";
import { useGlobalQueryParams as useGlobalQueryParamsDefault } from "./queryParams/useGlobalQueryParams";

interface UseFormUnitsParams<TUnit extends GroupableUnit> {
  channelUnits?: TUnit[] | null;
  unitGroupingMode?: UnitGroupingMode | null;
  unitGroups?: UnitGroupConfig[] | null;
  entry?: unknown;
  entryUnitIds?: string[] | null;
  useGlobalQueryParams?: typeof useGlobalQueryParamsDefault;
}

export type FormUnitsSource = "entry" | "group" | "channel";

export interface FormUnitsResult<TUnit extends GroupableUnit> {
  units: TUnit[];
  source: FormUnitsSource;
}

export function useFormUnits<TUnit extends GroupableUnit>({
  channelUnits,
  unitGroupingMode,
  unitGroups,
  entry,
  entryUnitIds: resolvedEntryUnitIds,
  useGlobalQueryParams,
}: UseFormUnitsParams<TUnit>): FormUnitsResult<TUnit> {
  const { selection } = useUnitSelection({
    useQueryParams: useGlobalQueryParams,
    restoreStoredChoice: false,
  });

  return useMemo(() => {
    const units = channelUnits || [];
    if (units.length === 0) return { units: [], source: "channel" as const };

    const entryUnitIds = resolvedEntryUnitIds ?? resolveEntryUnitIds({ entry });

    if (entryUnitIds.length > 0) {
      const byId = new Map(units.map((unit) => [unit.id, unit]));
      const resolved = entryUnitIds
        .map((unitId) => byId.get(unitId))
        .filter((unit): unit is TUnit => Boolean(unit));

      if (resolved.length > 0)
        return { units: resolved, source: "entry" as const };
    }

    if (selection.type !== "group")
      return { units, source: "channel" as const };

    const groups = buildUnitGroups({
      units,
      mode: unitGroupingMode,
      groups: unitGroups,
    });

    const groupUnitIds = unitIdsOfGroup(groups, selection.groupSlug);
    if (groupUnitIds.length === 0) return { units, source: "channel" as const };

    return {
      units: groupUnitIds
        .map((unitId) => units.find((unit) => unit.id === unitId))
        .filter((unit): unit is TUnit => Boolean(unit)),
      source: "group" as const,
    };
  }, [channelUnits, unitGroupingMode, unitGroups, entry, resolvedEntryUnitIds, selection]);
}
